import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetAppResponseBody } from '~/lib/api/modules/Apps';
import type { IUseOrganizationAttachAppMutationValues } from '~/lib/api/modules/Organizations/hooks/useOrganizationAttachAppMutation';
import type { IUseOrganizationDetachAppMutationValues } from '~/lib/api/modules/Organizations/hooks/useOrganizationDetachAppMutation';
import type { IAppOrganizationFormValues } from './AppOrganizationForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useOrganizationAttachAppMutation } from '~/lib/api/modules/Organizations/hooks/useOrganizationAttachAppMutation';
import { useOrganizationDetachAppMutation } from '~/lib/api/modules/Organizations/hooks/useOrganizationDetachAppMutation';
import { AppOrganizationForm } from './AppOrganizationForm';

type IUpdateAppOrganizationFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiGetAppResponseBody,
    inputValues:
      | IUseOrganizationAttachAppMutationValues
      | IUseOrganizationDetachAppMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const UpdateAppOrganizationForm: React.FC<
  IUpdateAppOrganizationFormProps
> = ({ sx, onSuccess, cancelUrl, appId }) => {
  const appQuery = useAppQuery({ appId });
  const { t } = useTranslation();
  const organizationAttachAppMutation = useOrganizationAttachAppMutation();
  const organizationDetachAppMutation = useOrganizationDetachAppMutation();

  const handleSubmit: SubmitHandler<IAppOrganizationFormValues> = (values) => {
    const currentOrganizationId = appQuery.data?.organization?._id;
    const newOrganizationId = values.organizationId;

    return newOrganizationId
      ? organizationAttachAppMutation.mutateAsync(
          {
            organizationId: newOrganizationId,
            appId,
          },
          { onSuccess },
        )
      : currentOrganizationId
        ? organizationDetachAppMutation.mutateAsync(
            {
              organizationId: currentOrganizationId,
              appId,
            },
            { onSuccess },
          )
        : undefined;
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>
        {t('UpdateAppOrganizationForm.title')}
      </ContentBlock.Title>
      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
      >
        {appQuery.data ? (
          <AppOrganizationForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              organizationId: appQuery.data.organization?._id ?? '',
            }}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
